import { Component, inject, signal, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, DestroyRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { TranslatePipe } from './i18n.service';
import { aucklandDateKey } from './auckland-time';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PhotoUploadService } from './photo-upload.service';
import { PhotoGalleryService } from './photo-gallery.service';

@Component({
  selector: 'app-project-execution',
  providers: [PhotoGalleryService],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslatePipe],
  template: `
    <div class="min-h-screen flex flex-col pb-8" style="background:#F7FBFD;">

      <!-- Header -->
      <header class="px-5 pt-4 pb-4 flex items-center gap-3 sticky top-0 z-30 text-white shadow-sm"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <button (click)="goBack()" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 backdrop-blur">
          <i class="ti ti-arrow-left text-[19px]"></i>
        </button>
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ 'project' | t }}</p>
          <h1 class="text-base font-semibold text-white/95 truncate">{{ project()?.name || 'Loading...' }}</h1>
        </div>
        <span class="text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-wider border backdrop-blur"
              [class.bg-emerald-400/15]="project()?.status === 'Completed'"
              [class.text-emerald-100]="project()?.status === 'Completed'"
              [class.border-emerald-200/25]="project()?.status === 'Completed'"
              [class.bg-white/10]="project()?.status !== 'Completed'"
              [class.text-white/75]="project()?.status !== 'Completed'"
              [class.border-white/15]="project()?.status !== 'Completed'">
          {{ project()?.status || '...' }}
        </span>
      </header>

      @if (isLoading()) {
        <div class="flex-1 flex items-center justify-center py-20">
          <i class="ti ti-loader animate-spin text-[40px] text-[#1A6FA6]"></i>
        </div>
      } @else if (project()) {

        <main class="flex-1 px-4 pt-5 space-y-4">

          <!-- Address -->
          @if (project()?.address) {
            <div class="bg-white rounded-2xl px-4 py-3 border border-slate-100 flex items-center gap-3">
              <i class="ti ti-map-pin text-[#1A6FA6] text-lg shrink-0"></i>
              <p class="text-sm font-normal text-slate-600 leading-snug">{{ project()?.address }}</p>
            </div>
          }

          @if (project()?.estimated_end_date) {
            <div class="bg-white rounded-2xl px-4 py-3 border border-slate-100 flex items-center gap-3">
              <i class="ti ti-calendar-due text-[#1A6FA6] text-lg shrink-0"></i>
              <div>
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Estimated Completion</p>
                <p class="text-sm font-semibold text-slate-800">{{ project()?.estimated_end_date | date:'d MMM y' }}</p>
              </div>
            </div>
          }

          <!-- Project Context -->
          <section class="bg-white rounded-3xl p-4 border border-slate-100">
            <div class="flex items-start justify-between gap-3 mb-3">
              <div class="min-w-0">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{{ 'projectDetails' | t }}</p>
                <h2 class="text-base font-semibold text-slate-900 truncate mt-0.5">{{ primaryContact()?.name || project()?.customer_name || 'Client' }}</h2>
                @if (project()?.description) {
                  <p class="text-sm text-slate-500 leading-snug mt-1">{{ project()?.description }}</p>
                }
              </div>
            </div>

            <div class="grid grid-cols-3 gap-2">
              @if (primaryContact()?.phone) {
                <a [href]="'tel:' + primaryContact()?.phone" class="h-11 rounded-2xl bg-sky-50 border border-sky-100 text-[#1A6FA6] text-xs font-semibold flex flex-col items-center justify-center gap-0.5 active:scale-[0.98]">
                  <i class="ti ti-phone text-base"></i> Call
                </a>
              }
              @if (primaryContact()?.email) {
                <a [href]="'mailto:' + primaryContact()?.email" class="h-11 rounded-2xl bg-slate-50 border border-slate-100 text-slate-600 text-xs font-semibold flex flex-col items-center justify-center gap-0.5 active:scale-[0.98]">
                  <i class="ti ti-mail text-base"></i> Email
                </a>
              }
              @if (project()?.address) {
                <a [href]="mapsUrl()" target="_blank" class="h-11 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-semibold flex flex-col items-center justify-center gap-0.5 active:scale-[0.98]">
                  <i class="ti ti-map-2 text-base"></i> Map
                </a>
              }
            </div>

            @if (projectContacts().length > 1) {
              <div class="mt-3 pt-3 border-t border-slate-100 space-y-2">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Other Contacts</p>
                @for (contact of projectContacts(); track contact.name + contact.phone + contact.email) {
                  @if (contact !== primaryContact()) {
                    <div class="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2">
                      <div class="min-w-0">
                        <p class="text-sm font-semibold text-slate-800 truncate">{{ contact.name || 'Contact' }}</p>
                        <p class="text-[11px] text-slate-500 truncate">{{ contact.contact_type || 'Contact' }} · {{ contact.phone || contact.email || 'No details' }}</p>
                      </div>
                      <div class="flex gap-1 shrink-0">
                        @if (contact.phone) {
                          <a [href]="'tel:' + contact.phone" class="w-9 h-9 rounded-xl bg-white border border-slate-100 text-[#1A6FA6] flex items-center justify-center"><i class="ti ti-phone"></i></a>
                        }
                        @if (contact.email) {
                          <a [href]="'mailto:' + contact.email" class="w-9 h-9 rounded-xl bg-white border border-slate-100 text-slate-500 flex items-center justify-center"><i class="ti ti-mail"></i></a>
                        }
                      </div>
                    </div>
                  }
                }
              </div>
            }
          </section>

          <!-- ════ TIMER STRIP ════ -->
          <div class="bg-white rounded-3xl p-5 border border-slate-100 relative overflow-hidden">
            <div class="flex items-center justify-between mb-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{{ 'currentShift' | t }}</p>
              <span class="text-[10px] font-semibold px-2.5 py-1 rounded-full capitalize"
                    [class.bg-slate-50]="sessionStatus() === 'none'"
                    [class.text-slate-500]="sessionStatus() === 'none'"
                    [class.bg-emerald-50]="sessionStatus() === 'active'"
                    [class.text-emerald-700]="sessionStatus() === 'active'"
                    [class.bg-amber-50]="sessionStatus() === 'paused'"
                    [class.text-amber-700]="sessionStatus() === 'paused'">
                {{ sessionStatus() === 'none' ? 'Not Started' : sessionStatus() }}
              </span>
            </div>
            <div class="font-mono text-[32px] font-semibold text-slate-900 mb-4 tracking-tight">{{ timerDisplay() }}</div>

            @if (!shiftActive() && (sessionStatus() === 'none' || sessionStatus() === 'paused')) {
              <div class="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2 text-xs text-amber-900">
                <i class="ti ti-alert-triangle-filled text-amber-600 shrink-0"></i>
                <span class="font-semibold flex-1">Please start your shift before starting or resuming this project.</span>
                <a routerLink="/" class="font-bold text-amber-700 underline underline-offset-2 whitespace-nowrap">Start Shift</a>
              </div>
            }

            <div class="flex gap-3">
              @if (sessionStatus() === 'none') {
                <button (click)="openSafetyConfirm()" [disabled]="isSaving() || shiftStatusLoading() || !shiftActive()"
                        class="flex-1 h-11 rounded-2xl bg-emerald-600 text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50 disabled:active:scale-100">
                  <i class="ti ti-player-play-filled"></i> {{ 'startWork' | t }}
                </button>
              }
              @if (sessionStatus() === 'active') {
                <button (click)="doSessionAction('pause')"
                        class="flex-1 h-11 rounded-2xl bg-amber-500 text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all">
                  <i class="ti ti-player-pause-filled"></i> Pause
                </button>
                <button (click)="doSessionAction('stop')"
                        class="h-11 px-5 rounded-2xl bg-rose-500 text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all">
                  <i class="ti ti-player-stop-filled"></i> Stop
                </button>
              }
              @if (sessionStatus() === 'paused') {
                <button (click)="doSessionAction('resume')" [disabled]="isSaving() || shiftStatusLoading() || !shiftActive()"
                        class="flex-1 h-11 rounded-2xl bg-emerald-600 text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50 disabled:active:scale-100">
                  <i class="ti ti-player-play-filled"></i> Resume
                </button>
                <button (click)="doSessionAction('stop')"
                        class="h-11 px-5 rounded-2xl bg-rose-500 text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all">
                  <i class="ti ti-player-stop-filled"></i> Stop
                </button>
              }
            </div>
          </div>

          <!-- ════ ASSIGNED WORKERS ════ -->
          @if (showSafetyConfirm()) {
            <div class="fixed inset-0 z-[80] flex items-center justify-center p-4" style="background:rgba(15,23,42,0.45);">
              <div class="bg-white rounded-2xl border border-slate-100 p-5 w-full max-w-[380px]">
                <div class="w-12 h-12 rounded-2xl mx-auto mb-4 bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <i class="ti ti-shield-check text-2xl"></i>
                </div>
                <h2 class="text-lg font-semibold text-slate-900 text-center">Safety Compliance</h2>
                <p class="text-sm text-slate-600 leading-relaxed mt-3">{{ safetyStatement }}</p>
                <label class="flex items-start gap-3 mt-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <input type="checkbox" [checked]="safetyAccepted()" (change)="safetyAccepted.set($any($event.target).checked)" class="mt-1">
                  <span class="text-sm text-slate-600">I have reviewed and accept this safety acknowledgement before starting work.</span>
                </label>
                <div class="mt-4 rounded-xl bg-rose-50 border border-rose-100 p-3 space-y-3">
                  <div>
                    <p class="text-xs font-semibold text-rose-800">Cannot accept?</p>
                    <p class="text-xs text-rose-600 mt-0.5">Record the safety concern before work starts. This will not start the project session.</p>
                  </div>
                  <textarea [(ngModel)]="safetyDeclineReason" rows="3" placeholder="Explain why this site is not safe to start..."
                            class="w-full px-3 py-2 rounded-xl border border-rose-100 bg-white outline-none text-sm text-slate-700 resize-none"></textarea>
                  <input #safetyPhotoInput type="file" accept="image/*" capture="environment" class="hidden" (change)="onSafetyPhotoSelected($event)">
                  @if (safetyDeclinePhoto()) {
                    <div class="flex items-center gap-3 rounded-xl bg-white border border-rose-100 p-2">
                      <img [src]="safetyDeclinePhoto()" alt="Safety concern photo" class="w-14 h-14 rounded-lg object-cover">
                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-semibold text-slate-700 truncate">{{ safetyDeclinePhotoName() || 'Photo attached' }}</p>
                        <button type="button" (click)="clearSafetyPhoto()" class="text-xs font-semibold text-rose-600 mt-1">Remove photo</button>
                      </div>
                    </div>
                  } @else {
                    <button type="button" (click)="safetyPhotoInput.click()" class="w-full h-10 rounded-xl bg-white border border-rose-100 text-rose-700 text-sm font-semibold flex items-center justify-center gap-2">
                      <i class="ti ti-camera"></i> Add Photo
                    </button>
                  }
                </div>
                <div class="grid grid-cols-2 gap-3 mt-5">
                  <button type="button" (click)="closeSafetyConfirm()" class="h-11 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm">Cancel</button>
                  <button type="button" (click)="confirmSafetyAndStart()" [disabled]="!safetyAccepted() || isSaving()"
                          class="h-11 rounded-xl bg-emerald-600 text-white font-semibold text-sm disabled:opacity-60">
                    @if (isSaving()) { <i class="ti ti-loader animate-spin"></i> } @else { Accept & Start }
                  </button>
                </div>
                <button type="button" (click)="declineSafetyAcknowledgement()" [disabled]="!safetyDeclineReason.trim() || isSaving()"
                        class="w-full h-11 rounded-xl bg-rose-600 text-white font-semibold text-sm disabled:opacity-60 mt-3 flex items-center justify-center gap-2">
                  @if (isSaving()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-shield-x"></i> Decline & Send to Admin }
                </button>
              </div>
            </div>
          }

          @if (project()?.assigned_workers?.length) {
            <div class="bg-white rounded-2xl px-4 py-3 border border-slate-100">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">{{ 'assigned' | t }}</p>
              <div class="flex flex-wrap gap-2">
                @for (w of project()?.assigned_workers; track w.id) {
                  <span class="bg-sky-50 text-sky-700 border border-sky-100 text-xs font-medium px-3 py-1 rounded-full">
                    <i class="ti ti-user mr-1"></i>{{ w.name }}
                  </span>
                }
              </div>
            </div>
          }

          <section class="bg-white rounded-3xl p-4 border border-slate-100">
            <div class="flex items-start justify-between gap-3 mb-3">
              <div>
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Project Photos</p>
                <h2 class="text-base font-semibold text-slate-900 mt-0.5">Site progress and evidence</h2>
                <p class="text-xs text-slate-500 mt-1">You can add photos whenever you are assigned to this project. A shift or project timer is not required.</p>
              </div>
              <span class="text-xs font-semibold text-slate-400">{{ photoGallery.total() }}</span>
            </div>

            <input #projectPhotoInput type="file" accept="image/*" multiple hidden (change)="onProjectPhotosSelected($event)">
            <button type="button" (click)="projectPhotoInput.click()" [disabled]="isQueuingPhotos()"
                    class="w-full h-12 rounded-2xl border-2 border-dashed border-sky-200 bg-sky-50 text-[#1A6FA6] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              <i class="ti ti-photo-plus"></i>
              Choose multiple photos
            </button>

            @if (projectPhotoFeedback()) { <p class="mt-2 text-xs font-semibold text-slate-600" role="status">{{ projectPhotoFeedback() }}</p> }
            @if (projectPhotoPreviews().length) {
              <div class="grid grid-cols-3 gap-2 mt-3">
                @for (preview of projectPhotoPreviews(); track preview.url; let index = $index) {
                  <div class="relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                    <img [src]="preview.url" alt="Selected project photo" class="w-full h-full object-cover">
                    <button type="button" (click)="removeProjectPhoto(index)" [disabled]="isQueuingPhotos()" class="absolute top-1 right-1 w-7 h-7 rounded-full bg-slate-950/75 text-white flex items-center justify-center">
                      <i class="ti ti-x"></i>
                    </button>
                  </div>
                }
              </div>
              <label class="block mt-3">
                <span class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Short comment (optional)</span>
                <textarea [(ngModel)]="projectPhotoComment" [disabled]="isQueuingPhotos()" maxlength="300" rows="2" placeholder="What do these photos show?"
                          class="w-full mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-[#1A6FA6] resize-none"></textarea>
                <span class="block text-right text-[10px] text-slate-400">{{ projectPhotoComment.length }}/300</span>
              </label>
              <button type="button" (click)="uploadSelectedProjectPhotos()" [disabled]="isQueuingPhotos()"
                      class="w-full h-11 mt-2 rounded-2xl bg-[#1A6FA6] text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                <i class="ti ti-upload"></i>
                {{ isQueuingPhotos() ? 'Saving to device...' : 'Upload ' + projectPhotoPreviews().length + ' photo(s)' }}
              </button>
            }

            @if (isLoadingProjectPhotos()) {
              <div class="py-8 text-center text-[#1A6FA6]"><i class="ti ti-loader animate-spin text-2xl"></i></div>
            } @else if (projectPhotos().length) {
              <div class="grid grid-cols-2 gap-3 mt-4">
                @for (photo of projectPhotos(); track photo.id; let photoIndex = $index) {
                  <article class="rounded-2xl overflow-hidden border border-slate-100 bg-slate-50">
                    <button type="button" (click)="openProjectPhotoViewer(photoIndex)" class="block w-full text-left relative">
                      <img [src]="photo.thumbnail_url || photo.photo_url || photo.photo_data" [alt]="photo.caption || 'Project photo'" class="w-full h-36 object-cover" loading="lazy" decoding="async">
                      <span class="absolute right-2 bottom-2 w-8 h-8 rounded-full bg-slate-950/65 text-white flex items-center justify-center"><i class="ti ti-maximize"></i></span>
                    </button>
                    <div class="p-3">
                      @if (photo.caption) { <p class="text-xs font-semibold text-slate-700 leading-snug mb-1">{{ photo.caption }}</p> }
                      <p class="text-[10px] text-slate-500 font-medium">{{ photo.uploader_name }}</p>
                      <p class="text-[10px] text-slate-400">{{ formatProjectPhotoTime(photo.created_at) }}</p>
                    </div>
                  </article>
                }
              </div>
            } @else {
              <p class="mt-4 py-4 text-center text-xs text-slate-400">No project photos uploaded yet.</p>
            }
            @if (photoGallery.error()) {
              <div role="alert" class="mt-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-700">
                {{ photoGallery.error() }} <button type="button" (click)="photoGallery.retry()" class="min-h-11 px-3 underline">Retry</button>
              </div>
            }
            @if (photoGallery.hasMore()) {
              <button type="button" (click)="photoGallery.loadMore()" [disabled]="photoGallery.loadingMore()" class="mt-3 w-full min-h-11 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700">
                {{ photoGallery.loadingMore() ? 'Loading photos...' : 'Load more photos' }}
              </button>
            }
          </section>

          <section class="bg-white rounded-3xl p-4 border border-slate-100">
            <div class="flex items-start justify-between gap-3 mb-3">
              <div>
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{{ 'asBuilt' | t }}</p>
                <h2 class="text-base font-semibold text-slate-900 mt-0.5">Project drainage drawing</h2>
              </div>
              <span class="text-xs font-semibold text-slate-400">{{ project()?.linked_documents?.as_built?.length || 0 }}</span>
            </div>

            @if (project()?.linked_documents?.as_built?.length) {
              <div class="space-y-2 mb-3">
                @for (doc of project()?.linked_documents?.as_built; track doc.id) {
                  <button type="button" (click)="openProjectAsBuilt(doc)" [disabled]="doc.status !== 'completed'"
                          class="w-full rounded-2xl bg-sky-50 border border-sky-100 px-3 py-3 text-left flex items-center justify-between gap-3 active:scale-[0.99] disabled:opacity-60">
                    <span class="min-w-0">
                      <span class="block text-sm font-semibold text-sky-800 truncate">{{ doc.title || 'As-built drawing' }}</span>
                      <span class="block text-[11px] text-sky-700 truncate">{{ doc.status === 'processing' ? 'AI processing' : (doc.doc_status || doc.status || 'As-built') }}</span>
                    </span>
                    <i class="ti ti-chevron-right text-sky-400"></i>
                  </button>
                }
              </div>
            } @else {
              <div class="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-5 text-center mb-3">
                <i class="ti ti-ruler-measure text-2xl text-slate-300"></i>
                <p class="text-sm font-semibold text-slate-700 mt-2">No as-built attached</p>
                <p class="text-xs text-slate-500 mt-0.5">Create one for this project only.</p>
              </div>
            }

            <div class="grid grid-cols-2 gap-2 rounded-2xl bg-slate-50 border border-slate-100 p-1 mb-3">
              <button type="button" (click)="asBuiltMode.set('full')"
                      class="h-10 rounded-xl text-xs font-semibold"
                      [class.bg-white]="asBuiltMode()==='full'"
                      [class.text-[#1A6FA6]]="asBuiltMode()==='full'"
                      [class.shadow-sm]="asBuiltMode()==='full'"
                      [class.text-slate-500]="asBuiltMode()!=='full'">
                Full draft
              </button>
              <button type="button" (click)="asBuiltMode.set('boundary_only')"
                      class="h-10 rounded-xl text-xs font-semibold"
                      [class.bg-white]="asBuiltMode()==='boundary_only'"
                      [class.text-[#1A6FA6]]="asBuiltMode()==='boundary_only'"
                      [class.shadow-sm]="asBuiltMode()==='boundary_only'"
                      [class.text-slate-500]="asBuiltMode()!=='boundary_only'">
                Boundary only
              </button>
            </div>

            <input type="file" #asBuiltInput accept="image/*" hidden (change)="uploadProjectAsBuilt($event)">
            <div class="grid grid-cols-2 gap-3">
              <button type="button" (click)="asBuiltInput.click()" [disabled]="isUploadingAsBuilt()"
                      class="h-11 rounded-2xl bg-[#1A6FA6] text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                @if (isUploadingAsBuilt()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-upload"></i> }
                Upload
              </button>
              <button type="button" (click)="createProjectAsBuilt()" [disabled]="isCreatingAsBuilt()"
                      class="h-11 rounded-2xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
                @if (isCreatingAsBuilt()) { <i class="ti ti-loader animate-spin"></i> } @else { <i class="ti ti-file-plus"></i> }
                Blank
              </button>
            </div>
          </section>

          <!-- Documents -->
          @if (hasProjectDocuments()) {
            <section class="bg-white rounded-3xl p-4 border border-slate-100">
              <div class="flex items-center justify-between mb-3">
                <div>
                  <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Documents</p>
                  <h2 class="text-base font-semibold text-slate-900 mt-0.5">Project Files</h2>
                </div>
                <span class="text-xs font-semibold text-slate-400">{{ documentCount() }}</span>
              </div>

              @if (project()?.documents?.length) {
                <div class="space-y-2">
                  @for (doc of project()?.documents; track doc.id) {
                    <button type="button" (click)="openProjectDocument(doc)"
                            class="w-full rounded-2xl border border-slate-100 bg-slate-50 px-3 py-3 text-left flex items-center gap-3 active:scale-[0.99]">
                      <span class="w-9 h-9 rounded-xl bg-white border border-slate-100 text-[#1A6FA6] flex items-center justify-center shrink-0"><i class="ti ti-file-text"></i></span>
                      <span class="min-w-0 flex-1">
                        <span class="block text-sm font-semibold text-slate-800 truncate">{{ doc.name }}</span>
                        <span class="block text-[11px] text-slate-500 truncate">{{ doc.document_type || 'General' }}</span>
                      </span>
                      <i class="ti ti-chevron-right text-slate-300"></i>
                    </button>
                  }
                </div>
              }

              @if (project()?.linked_documents?.insurance_reports?.length) {
                <div class="mt-3 pt-3 border-t border-slate-100 space-y-2">
                  @if (project()?.linked_documents?.insurance_reports?.length) {
                    @for (report of project()?.linked_documents?.insurance_reports; track report.id) {
                      <button type="button" (click)="openGeneratedDocument('insurance', report.id)"
                              class="w-full rounded-2xl bg-emerald-50 border border-emerald-100 px-3 py-3 text-left flex items-center justify-between gap-3 active:scale-[0.99]">
                        <span class="min-w-0">
                          <span class="block text-sm font-semibold text-emerald-800 truncate">{{ report.report_no || 'Insurance report' }}</span>
                          <span class="block text-[11px] text-emerald-700 truncate">{{ report.status || 'Report' }}</span>
                        </span>
                        <i class="ti ti-chevron-right text-emerald-400"></i>
                      </button>
                    }
                  }
                </div>
              }

              @if (completedCertificates().length) {
                <div class="mt-3 pt-3 border-t border-slate-100 space-y-2">
                  <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Certificates</p>
                  @for (cert of completedCertificates(); track cert.type) {
                    <button type="button" (click)="openProjectCertificate(cert.type)"
                            class="w-full rounded-2xl bg-indigo-50 border border-indigo-100 px-3 py-3 text-left flex items-center justify-between gap-3 active:scale-[0.99]">
                      <span class="min-w-0">
                        <span class="block text-sm font-semibold text-indigo-800 truncate">{{ cert.label }}</span>
                        <span class="block text-[11px] text-indigo-700 truncate">View only · updated {{ cert.updated_at | date:'d MMM y' }}</span>
                      </span>
                      <i class="ti ti-file-type-pdf text-indigo-500"></i>
                    </button>
                  }
                </div>
              }
            </section>
          }

          <!-- ════ PHASES ════ -->
          <div>
            <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">Project Phases</p>
            <div class="space-y-3">
              @for (phase of project()?.phases; track phase.id) {
                <div class="bg-white rounded-2xl border border-slate-100 p-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-3">
                      <div class="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center text-[#1A6FA6] shrink-0">
                        <i class="ti ti-clipboard-list text-lg"></i>
                      </div>
                      <div>
                        <h3 class="font-semibold text-sm text-slate-800">{{ phase.phase_name }}</h3>
                        <span class="text-[10px] font-medium px-2 py-0.5 rounded-full"
                              [class.bg-emerald-100]="phase.status === 'Completed'"
                              [class.text-emerald-700]="phase.status === 'Completed'"
                              [class.bg-sky-100]="phase.status === 'Submitted'"
                              [class.text-sky-700]="phase.status === 'Submitted'"
                              [class.bg-rose-100]="phase.status === 'Needs Changes'"
                              [class.text-rose-700]="phase.status === 'Needs Changes'"
                              [class.bg-amber-100]="phase.status === 'In Progress'"
                              [class.text-amber-700]="phase.status === 'In Progress'"
                              [class.bg-slate-100]="phase.status === 'Pending'"
                              [class.text-slate-500]="phase.status === 'Pending'">
                          {{ phase.status }}
                        </span>
                      </div>
                    </div>
                  </div>

                  @if (phase.phase_name === 'Second-Fix' && !isFirstFixDone()) {
                    <!-- Locked State -->
                    <div class="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                      <i class="ti ti-lock text-base shrink-0"></i>
                      <p class="text-xs font-medium">First Fix must be approved before accessing Second Fix</p>
                    </div>
                  } @else if (phase.status === 'Pending') {
                    <div class="flex items-center gap-2 text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
                      <i class="ti ti-clock text-base shrink-0"></i>
                      <p class="text-xs font-medium">Waiting for office to start this phase</p>
                    </div>
                  } @else {
                    @if (phase.review_notes) {
                      <div class="mb-3 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2">
                        <p class="text-[10px] font-semibold uppercase tracking-widest text-amber-700">Office note</p>
                        <p class="text-xs text-amber-800 leading-snug mt-1">{{ phase.review_notes }}</p>
                      </div>
                    }
                    <button (click)="openPhaseForm(phase)"
                            class="w-full h-10 rounded-xl bg-[#1A6FA6] text-white font-semibold flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-all">
                      <i class="ti ti-file-pencil"></i>
                      {{ phase.form_data ? 'View / Edit Form' : 'Fill Form' }}
                    </button>
                  }
                </div>
              }
            </div>
          </div>

        </main>

        <!-- ════════════════════════════════════════════
             PHASE FORM MODAL (First Fix / Second Fix)
             ════════════════════════════════════════════ -->
        @if (showModal()) {
          <div class="fixed inset-0 z-50 flex flex-col" style="background:rgba(15,23,42,0.45);">
            <div class="flex-1 overflow-auto bg-white mt-16 rounded-t-3xl flex flex-col">
              <!-- Modal Header -->
              <div class="sticky top-0 bg-white border-b border-slate-100 px-5 py-3.5 flex items-center gap-3 z-10">
                <button (click)="closeModal()" class="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500">
                  <i class="ti ti-x"></i>
                </button>
                <h2 class="font-semibold text-slate-900 text-base flex-1">{{ activePhase()?.phase_name }} Form</h2>
                <button (click)="submitPhaseForm('save_draft')" [disabled]="isSaving()"
                        class="h-9 px-3 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
                  Draft
                </button>
                <button (click)="submitPhaseForm('submit')" [disabled]="isSaving()"
                        class="h-9 px-4 rounded-xl bg-[#1A6FA6] text-white font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
                  @if (isSaving()) {
                    <i class="ti ti-loader animate-spin"></i>
                  } @else {
                    <i class="ti ti-check"></i>
                  }
                  Submit
                </button>
              </div>

              <div class="flex-1 overflow-auto p-5 space-y-4">

                <!-- Technician Details -->
                <section class="bg-white rounded-2xl p-4 border border-slate-100">
                  <h3 class="text-xs font-semibold text-[#1A6FA6] uppercase tracking-widest mb-3">Technician Details</h3>
                  <div class="space-y-3">
                    <div>
                      <label class="block text-xs font-medium text-slate-500 mb-1">Full Name</label>
                      <input type="text" [(ngModel)]="formData().techDetails.fullName"
                             class="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm font-normal">
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label class="block text-xs font-medium text-slate-500 mb-1">Date</label>
                        <input type="date" [(ngModel)]="formData().techDetails.date"
                               class="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                        <input type="tel" [(ngModel)]="formData().techDetails.phone"
                               class="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                      </div>
                    </div>
                    <div class="grid grid-cols-3 gap-3">
                      <div>
                        <label class="block text-xs font-medium text-slate-500 mb-1">Plumbing Lic.</label>
                        <input type="text" [(ngModel)]="formData().techDetails.plumbingLicence"
                               class="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-slate-500 mb-1">Drainage Lic.</label>
                        <input type="text" [(ngModel)]="formData().techDetails.drainageLicence"
                               class="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-slate-500 mb-1">Gas Lic.</label>
                        <input type="text" [(ngModel)]="formData().techDetails.gasLicence"
                               class="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Dynamic Checklist Sections for First Fix -->
                @if (activePhase()?.phase_name === 'First-Fix') {
                  @for (section of firstFixSections; track section.key) {
                    <section class="bg-white rounded-2xl p-4 border border-slate-100">
                      <h3 class="text-xs font-semibold text-[#1A6FA6] uppercase tracking-widest mb-3">{{ section.label }}</h3>
                      <div class="space-y-2">
                        @for (item of section.items; track item) {
                          <label class="flex items-start gap-3 cursor-pointer py-1.5 group">
                            <div class="relative shrink-0 mt-0.5">
                              <input type="checkbox" [ngModel]="getCheck(section.key, item)"
                                     (ngModelChange)="setCheck(section.key, item, $event)" class="peer sr-only">
                              <div class="w-5 h-5 rounded-md border transition-all flex items-center justify-center"
                                   [ngClass]="getCheck(section.key, item) ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300'">
                                <i class="ti ti-check text-white text-xs"></i>
                              </div>
                            </div>
                            <span class="text-[13px] text-slate-600 leading-snug pt-0.5">{{ item }}</span>
                          </label>
                        }
                      </div>
                    </section>
                  }
                }

                <!-- Dynamic Checklist Sections for Second Fix -->
                @if (activePhase()?.phase_name === 'Second-Fix') {
                  @for (section of secondFixSections; track section.key) {
                    <section class="bg-white rounded-2xl p-4 border border-slate-100">
                      <h3 class="text-xs font-semibold text-[#1A6FA6] uppercase tracking-widest mb-3">{{ section.label }}</h3>
                      <div class="space-y-2">
                        @for (item of section.items; track item) {
                          <label class="flex items-start gap-3 cursor-pointer py-1.5 group">
                            <div class="relative shrink-0 mt-0.5">
                              <input type="checkbox" [ngModel]="getCheck(section.key, item)"
                                     (ngModelChange)="setCheck(section.key, item, $event)" class="peer sr-only">
                              <div class="w-5 h-5 rounded-md border transition-all flex items-center justify-center"
                                   [ngClass]="getCheck(section.key, item) ? 'bg-[#1A6FA6] border-[#1A6FA6]' : 'bg-white border-slate-300'">
                                <i class="ti ti-check text-white text-xs"></i>
                              </div>
                            </div>
                            <span class="text-[13px] text-slate-600 leading-snug pt-0.5">{{ item }}</span>
                          </label>
                        }
                      </div>
                    </section>
                  }
                }

                <!-- Notes -->
                <section class="bg-white rounded-2xl p-4 border border-slate-100">
                  <h3 class="text-xs font-semibold text-[#1A6FA6] uppercase tracking-widest mb-3">Additional Notes</h3>
                  <textarea [(ngModel)]="formData().notes" rows="4" placeholder="Any findings or observations..."
                            class="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm resize-none"></textarea>
                </section>

                <!-- Signature -->
                <section class="bg-white rounded-2xl p-4 border border-slate-100">
                  <div class="flex justify-between items-center mb-3">
                    <h3 class="text-xs font-semibold text-[#1A6FA6] uppercase tracking-widest">Signature</h3>
                    <button (click)="clearSig()" class="text-xs font-medium text-slate-400 hover:text-slate-700">Clear</button>
                  </div>
                  <div class="w-full h-[120px] bg-slate-50 rounded-xl border border-dashed border-slate-300 relative overflow-hidden"
                       [class.border-[#1A6FA6]]="!sigEmpty">
                    @if (sigEmpty) {
                      <div class="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
                        <p class="text-lg font-medium font-serif text-slate-400">Sign Here</p>
                      </div>
                    }
                    <canvas #sigPad
                            class="absolute inset-0 w-full h-full cursor-crosshair touch-none"
                            (mousedown)="startSig($event)" (mousemove)="drawSig($event)"
                            (mouseup)="stopSig()" (mouseleave)="stopSig()"
                            (touchstart)="startSig($event)" (touchmove)="drawSig($event)"
                            (touchend)="stopSig()"></canvas>
                  </div>
                </section>

              </div>
            </div>
          </div>
        }

      } @else {
        <main class="flex-1 p-8 flex flex-col items-center justify-center text-center">
          <div class="w-16 h-16 rounded-2xl bg-white border border-red-100 shadow-sm flex items-center justify-center text-red-400 mb-4">
            <i class="ti ti-alert-circle text-[32px]"></i>
          </div>
          <h2 class="text-xl font-black text-slate-900 mb-2">{{ loadError() || 'Project Not Found' }}</h2>
          <p class="text-slate-500 text-sm leading-relaxed max-w-xs">This project may have been removed or is no longer assigned to your account.</p>
          <button (click)="loadProject()" class="mt-6 h-12 px-6 rounded-2xl bg-[#1A6FA6] text-white font-bold shadow-sm">
            Retry
          </button>
        </main>
      }

      @if (selectedProjectPhoto()) {
        <div class="fixed inset-0 z-[100] bg-slate-950/95 flex flex-col" (click)="closeProjectPhotoViewer()">
          <div class="h-16 px-4 flex items-center justify-between gap-3 text-white border-b border-white/10" (click)="$event.stopPropagation()">
            <div class="min-w-0"><p class="text-[10px] uppercase tracking-widest text-white/45 font-semibold">Project Photo</p><p class="text-sm font-semibold truncate">{{ selectedProjectPhoto()?.caption || 'Site photo' }}</p></div>
            <div class="flex items-center gap-2">
              <button type="button" (click)="downloadSelectedProjectPhoto()" [disabled]="downloadingProjectPhotoId() === selectedProjectPhoto()?.id" class="h-10 px-3 rounded-xl bg-white/10 text-white flex items-center gap-2 disabled:opacity-50"><i [class]="downloadingProjectPhotoId() === selectedProjectPhoto()?.id ? 'ti ti-loader animate-spin' : 'ti ti-download'"></i><span class="text-xs font-semibold">Download</span></button>
              <button type="button" (click)="closeProjectPhotoViewer()" class="w-10 h-10 rounded-xl bg-white/10 text-white"><i class="ti ti-x text-xl"></i></button>
            </div>
          </div>
          <div class="relative flex-1 min-h-0 flex items-center justify-center p-3" (click)="$event.stopPropagation()">
            @if (projectPhotos().length > 1) {
              <button type="button" (click)="showPreviousProjectPhoto()" class="absolute left-2 z-10 w-11 h-11 rounded-full bg-slate-950/60 border border-white/15 text-white"><i class="ti ti-chevron-left text-xl"></i></button>
              <button type="button" (click)="showNextProjectPhoto()" class="absolute right-2 z-10 w-11 h-11 rounded-full bg-slate-950/60 border border-white/15 text-white"><i class="ti ti-chevron-right text-xl"></i></button>
            }
            <img [src]="selectedProjectPhoto()?.photo_url || selectedProjectPhoto()?.photo_data" [alt]="selectedProjectPhoto()?.caption || 'Project photo'" class="max-w-full max-h-full object-contain rounded-xl">
          </div>
          <div class="px-4 py-4 border-t border-white/10 text-white" (click)="$event.stopPropagation()">
            <p class="text-sm font-semibold">{{ selectedProjectPhoto()?.caption || 'No comment added.' }}</p>
            <p class="text-xs text-white/50 mt-1">{{ selectedProjectPhoto()?.uploader_name || 'Unknown uploader' }} · {{ formatProjectPhotoTime(selectedProjectPhoto()?.created_at) }}</p>
            <p class="text-[10px] text-white/35 mt-1">{{ projectPhotoViewerIndex()! + 1 }} of {{ projectPhotos().length }} loaded photos</p>
          </div>
        </div>
      }

    </div>
  `
})
export class ProjectExecutionComponent implements OnInit, OnDestroy {
  api = inject(ApiService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  location = inject(Location);
  private readonly uploads = inject(PhotoUploadService);
  private readonly destroyRef = inject(DestroyRef);
  projectPhotoFeedback = signal('');
  readonly photoGallery = inject(PhotoGalleryService);
  readonly isQueuingPhotos = signal(false);

  projectId = signal('');
  project = signal<any>(null);
  isLoading = signal(true);
  loadError = signal('');
  isSaving = signal(false);
  asBuiltMode = signal<'full' | 'boundary_only'>('boundary_only');
  isUploadingAsBuilt = signal(false);
  isCreatingAsBuilt = signal(false);
  projectPhotos = this.photoGallery.photos;
  projectPhotoPreviews = signal<Array<{ file: File; url: string }>>([]);
  isLoadingProjectPhotos = this.photoGallery.loading;
  projectPhotoViewerIndex = signal<number | null>(null);
  downloadingProjectPhotoId = signal<number | null>(null);
  projectPhotoComment = '';
  showSafetyConfirm = signal(false);
  safetyAccepted = signal(false);
  safetyDeclinePhoto = signal<string | null>(null);
  safetyDeclinePhotoName = signal('');
  safetyDeclineReason = '';
  safetyStatement = 'Before starting work, I confirm that I have assessed the site and will work in accordance with NZ Plumbing safety procedures and New Zealand Health and Safety at Work expectations. I will take reasonable care for my own health and safety, ensure my actions do not put other people at risk, follow reasonable instructions and policies, use required PPE, and stop work or raise concerns if conditions are unsafe.';

  // Timer
  shiftActive = this.api.currentShiftActive;
  shiftStatusLoading = signal(true);
  sessionStatus = signal<'none'|'active'|'paused'>('none');
  timerDisplay = signal('00:00:00');
  accumulatedSeconds = 0;
  sessionStarted!: Date;
  timerInterval: any;
  private sessionSyncInterval: ReturnType<typeof setInterval> | undefined;
  private sessionSyncing = false;
  private readonly handleVisibilityChange = () => {
    if (!document.hidden) {
      this.syncSessionState();
    }
  };
  private readonly handleWindowFocus = () => this.syncSessionState();
  private readonly handleProjectSessionChanged = () => this.syncSessionState();

  // Modal
  showModal = signal(false);
  activePhase = signal<any>(null);
  formData = signal<any>({
    techDetails: { fullName: '', date: aucklandDateKey(), phone: '', plumbingLicence: '', drainageLicence: '', gasLicence: '' },
    checks: {} as Record<string, Record<string, boolean>>,
    notes: ''
  });

  // Signature
  sigPad!: ElementRef<HTMLCanvasElement>;
  @ViewChild('sigPad') set signaturePadRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref) {
      this.sigPad = ref;
      setTimeout(() => this.initSigCanvas(), 50);
    }
  }
  sigCtx!: CanvasRenderingContext2D;
  sigDrawing = false;
  sigEmpty = true;

  // Checklist data
  firstFixSections = [
    { key: 'hydraulic_setout', label: 'Hydraulic – Setout', items: ['Pipework run to Current Construction set drawings', 'Discharge units calculated for fixtures waste system', 'Copies of taps & fittings schedule supplied', 'Hot water supply lines run the shortest', 'Copies of taps & fittings set out schedule supplied', 'Fittings and Fixtures confirmed & on order?', 'Checked Soil and waste pipes for minimum fall', 'Hydraulics checked vs Architectural drawings'] },
    { key: 'hydraulic_pipework', label: 'Hydraulic – Pipework/Installation', items: ['Specified waste pipe work is acoustic lagged', 'Penetrations through fire cells are fire protected', 'All Joist Brackets or Reinforcement installed if required', "Nog's and fixings installed for WC,WHB,SHW,HWC?", 'Penetrations through building paper are trade sealed', 'Vent penetration has been back flashed and sealed', 'All exposed pipe work is correctly labeled', 'Hot Water Ring main is thermally lagged', 'All pipe work clipped neatly and tidy', 'Waterman connected to water meter & building', 'Water main Supply is under 500kpa', 'All pipework secured/siliconed', 'Pre-line plumbing inspection booked', 'FWC flange recessed into flooring and installed'] },
    { key: 'test_completion', label: 'Test & Completion Documents', items: ['Water tested to 1500kpa and loop not affect lining', 'Test plug & Gauge removed from pipe system', 'Checked for drips on water and waste piping', 'Picked up all left over material and removed from site', 'Soil and waste piping hydrostatic test (Flood Test)', 'Backflow certification complete/booked', 'Fire penetration Schedule completed', 'As built completed and handed in', 'Gas pipe work tested to twice the working pressure'] },
    { key: 'drainage', label: 'Drainage Pipework Installation', items: ['All drainage set to minimum fall', 'Long radius bends used above 67.5 degrees', 'Access provided to all junctions and bends', 'Vent pipes set to minimum of 1 in 4', 'All drains clipped adequately and supported', 'Confirmed drain depth sufficient for head requirements', 'All finished surface levels determined', 'All drainage pipework material confirmed'] },
    { key: 'drainage_fillout', label: 'Drainage Fillout', items: ['Drain connection to sewer/septic confirmed', 'All penetrations sealed after inspection', 'CCTV inspection booked if required', 'Drainage as-built completed'] }
  ];

  secondFixSections = [
    { key: 'hydraulic_setout_sf', label: 'Hydraulic – Setout', items: ['All fixtures installed per schedule', 'Hot and cold supply final connections made', 'Isolation valves fitted at all fixtures', 'Final pressure test completed', 'All fixtures commissioned and functioning'] },
    { key: 'tapware_fitoff', label: 'Tapware & Whiteware – Fitoff', items: ['Basin mixer installed and checked for leaks', 'Bath mixer installed and checked for leaks', 'Shower rose and arm installed', 'Kitchen sink mixer installed', 'All toilet suites installed and flushing correctly', 'WC cistern water level set correctly', 'Laundry tub installed and draining', 'All tapware handles and trim fitted', 'Flow restrictors fitted where required'] },
    { key: 'gas_heater', label: 'Gas Water Heater & Regulator', items: ['Hot water cylinder installed and connected', 'Gas regulator installed and pressure set', 'Gas supply line pressure tested', 'HWC relief valve installed and piped to drain', 'Gas cooktop connected and burners tested', 'Flue/venting installed correctly', 'Gas certificate completed and filed'] }
  ];

  get isFirstFixDone(): () => boolean {
    return () => {
      const phases = this.project()?.phases || [];
      const ff = phases.find((p: any) => p.phase_name === 'First-Fix');
      return ['Approved', 'Completed'].includes(ff?.status);
    };
  }

  projectContacts(): any[] {
    return this.project()?.contacts || [];
  }

  primaryContact(): any | null {
    const contacts = this.projectContacts();
    return contacts.find((contact: any) => contact.contact_type === 'Customer' || contact.type === 'Customer')
      || contacts[0]
      || null;
  }

  mapsUrl(): string {
    return `https://maps.google.com/?q=${encodeURIComponent(this.project()?.address || '')}`;
  }

  hasProjectDocuments(): boolean {
    const project = this.project();
    return !!(
      project?.documents?.length ||
      project?.linked_documents?.insurance_reports?.length ||
      this.completedCertificates().length
    );
  }

  documentCount(): number {
    const project = this.project();
    return Number(project?.documents?.length || 0)
      + Number(project?.linked_documents?.insurance_reports?.length || 0)
      + this.completedCertificates().length;
  }

  completedCertificates(): any[] {
    const certs = this.project()?.certificates || {};
    return [
      { type: 'PS3_Plumbing', label: 'PS3 Plumbing', updated_at: certs.PS3_Plumbing?.updated_at },
      { type: 'PS3_Drainage', label: 'PS3 Drainage', updated_at: certs.PS3_Drainage?.updated_at },
      { type: 'Gas_Safety', label: 'Gas Safety', updated_at: certs.Gas_Safety?.updated_at }
    ].filter(cert => !!cert.updated_at);
  }

  openProjectDocument(doc: any) {
    if (!doc?.id) return;
    const token = encodeURIComponent(this.api.accessToken());
    window.location.href = `${this.api.baseUrl}/projects/document_view.php?id=${encodeURIComponent(doc.id)}&token=${token}`;
  }

  openProjectAsBuilt(doc: any) {
    if (!doc?.id) return;
    this.router.navigate(['/as-built', doc.id]);
  }

  createProjectAsBuilt() {
    const projectId = this.projectId();
    if (!projectId || this.isCreatingAsBuilt()) return;
    this.isCreatingAsBuilt.set(true);
    this.api.createManualAsBuiltPlan({
      title: `As-built - ${this.project()?.name || projectId}`,
      drawing_width_metres: 30,
      reference_id: projectId,
      model_type: 'Project'
    }).subscribe({
      next: (res: any) => {
        this.isCreatingAsBuilt.set(false);
        if (res?.success && res.data?.job_id) {
          showToast('success', 'As-built created for this project.');
          this.router.navigate(['/as-built', res.data.job_id]);
        } else {
          showToast('error', res?.message || 'Could not create as-built.');
        }
      },
      error: () => {
        this.isCreatingAsBuilt.set(false);
        showToast('error', 'Network error creating as-built.');
      }
    });
  }

  uploadProjectAsBuilt(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const projectId = this.projectId();
    if (!file || !projectId || this.isUploadingAsBuilt()) return;

    this.isUploadingAsBuilt.set(true);
    this.api.uploadAsBuiltPlan(file, {
      title: `As-built - ${this.project()?.name || file.name.replace(/\.[^.]+$/, '')}`,
      extraction_mode: this.asBuiltMode(),
      reference_id: projectId,
      model_type: 'Project'
    }).subscribe({
      next: (res: any) => {
        this.isUploadingAsBuilt.set(false);
        if (res?.success) {
          showToast('success', 'As-built AI job started for this project.');
          this.loadProject();
        } else {
          showToast('error', res?.message || 'Upload failed.');
        }
      },
      error: () => {
        this.isUploadingAsBuilt.set(false);
        showToast('error', 'Network error uploading as-built.');
      }
    });
  }

  openGeneratedDocument(type: 'as-built' | 'insurance', id: string | number) {
    if (!id) return;
    if (type === 'as-built') {
      this.router.navigate(['/as-built', id]);
      return;
    }
    const base = window.location.origin;
    window.location.href = `${base}/admin/ai/insurance/${id}`;
  }

  openProjectCertificate(type: string) {
    const scriptMap: Record<string, string> = {
      PS3_Plumbing: 'generate_ps3_plumbing.php',
      PS3_Drainage: 'generate_ps3_drainage.php',
      Gas_Safety: 'generate_gas_certificate.php'
    };
    const script = scriptMap[type];
    if (!script || !this.projectId()) return;
    const token = encodeURIComponent(this.api.accessToken());
    window.location.href = `${this.api.baseUrl}/projects/${script}?id=${encodeURIComponent(this.projectId())}&token=${token}`;
  }

  ngOnInit() {
    this.uploads.saved.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event.modelType === 'Project' && event.referenceId === this.projectId()) {
        this.photoGallery.add(event.photo);
      }
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);
    window.addEventListener('nzp-project-session-changed', this.handleProjectSessionChanged);
    this.sessionSyncInterval = setInterval(() => this.syncSessionState(), 30000);
    this.loadShiftStatus();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(p => {
      const id = p.get('id');
      if (id) {
        this.clearProjectPhotoSelection();
        this.projectPhotoViewerIndex.set(null);
        this.projectId.set(id);
        this.loadProject();
        this.loadProjectPhotos();
      }
    });
    const user = this.api.currentUser();
    if (user) {
      this.formData.update(f => ({ ...f, techDetails: { ...f.techDetails, fullName: user.name || '' } }));
    }
  }

  loadProject() {
    this.isLoading.set(true);
    this.loadError.set('');
    this.stopTicking();
    this.sessionStatus.set('none');
    this.accumulatedSeconds = 0;
    this.timerDisplay.set('00:00:00');
    this.api.getProject(this.projectId()).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.project.set(res.data);
          this.applySessionState(res.data.active_session || null);
        } else {
          this.project.set(null);
          this.loadError.set(res.message || 'Unable to load project.');
        }
      },
      error: (err: any) => {
        this.project.set(null);
        this.loadError.set(err?.error?.message || 'Unable to load project.');
        this.isLoading.set(false);
      }
    });
  }

  loadProjectPhotos() { this.photoGallery.reset(this.projectId(), 'Project'); }

  onProjectPhotosSelected(event: Event) {
    if (this.isQueuingPhotos()) return;
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    const remaining = Math.max(0, 10 - this.projectPhotoPreviews().length);
    const selected = files.slice(0, remaining);
    this.projectPhotoPreviews.update(current => [...current, ...selected.map(file => ({ file, url: URL.createObjectURL(file) }))]);
    this.projectPhotoFeedback.set(files.length > remaining ? 'A maximum of 10 photos can be selected at once.' : '');
  }

  removeProjectPhoto(index: number) {
    if (this.isQueuingPhotos()) return;
    const current = this.projectPhotoPreviews();
    const removed = current[index];
    if (removed) URL.revokeObjectURL(removed.url);
    this.projectPhotoPreviews.set(current.filter((_, itemIndex) => itemIndex !== index));
  }

  async uploadSelectedProjectPhotos() {
    const selected = this.projectPhotoPreviews();
    if (!selected.length || this.isQueuingPhotos()) return;
    this.isQueuingPhotos.set(true);
    try {
      await this.uploads.enqueue(this.projectId(), 'Project', selected.map(item => item.file), this.projectPhotoComment);
      if (this.projectPhotoPreviews() === selected) {
        this.clearProjectPhotoSelection();
        this.projectPhotoFeedback.set('Photos saved to this device and queued. Unfinished uploads resume when you reopen the app.');
      }
    } catch (error) {
      this.projectPhotoFeedback.set(error instanceof Error ? error.message : 'Photos could not be queued.');
    } finally { this.isQueuingPhotos.set(false); }
  }

  private clearProjectPhotoSelection() {
    this.projectPhotoPreviews().forEach(item => URL.revokeObjectURL(item.url));
    this.projectPhotoPreviews.set([]);
    this.projectPhotoComment = '';
  }



  formatProjectPhotoTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value || '';
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    }).format(date);
  }

  selectedProjectPhoto(): any | null {
    const index = this.projectPhotoViewerIndex();
    return index === null ? null : this.projectPhotos()[index] || null;
  }

  openProjectPhotoViewer(index: number) {
    if (index >= 0 && index < this.projectPhotos().length) this.projectPhotoViewerIndex.set(index);
  }

  closeProjectPhotoViewer() { this.projectPhotoViewerIndex.set(null); }

  showPreviousProjectPhoto() {
    const total = this.projectPhotos().length;
    const current = this.projectPhotoViewerIndex();
    if (total && current !== null) this.projectPhotoViewerIndex.set((current - 1 + total) % total);
  }

  showNextProjectPhoto() {
    const total = this.projectPhotos().length;
    const current = this.projectPhotoViewerIndex();
    if (total && current !== null) this.projectPhotoViewerIndex.set((current + 1) % total);
  }

  downloadSelectedProjectPhoto() {
    const photo = this.selectedProjectPhoto();
    if (!photo?.id || this.downloadingProjectPhotoId() !== null) return;
    this.downloadingProjectPhotoId.set(Number(photo.id));
    this.api.downloadPhoto(photo.id).subscribe({
      next: blob => {
        this.downloadingProjectPhotoId.set(null);
        const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : blob.type === 'image/gif' ? 'gif' : 'jpg';
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `project-photo-${photo.id}.${extension}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      },
      error: () => {
        this.downloadingProjectPhotoId.set(null);
        showToast('error', 'Photo could not be downloaded.');
      }
    });
  }

  private syncSessionState() {
    const projectId = this.projectId();
    if (!projectId || this.sessionSyncing || this.isLoading()) return;
    this.sessionSyncing = true;
    this.api.getProjectWorkSessions(projectId).subscribe({
      next: (res: any) => {
        this.sessionSyncing = false;
        if (res?.success) this.applySessionState(res.data?.active_session || null);
      },
      error: () => {
        this.sessionSyncing = false;
      }
    });
  }

  private applySessionState(session: any | null) {
    if (!session) {
      this.stopTicking();
      this.sessionStatus.set('none');
      this.accumulatedSeconds = 0;
      this.timerDisplay.set('00:00:00');
      return;
    }

    this.accumulatedSeconds = Number(session.current_total_seconds ?? session.total_seconds ?? 0);
    if (session.status === 'active') {
      this.sessionStatus.set('active');
      this.sessionStarted = new Date();
      this.startTicking();
      return;
    }

    this.stopTicking();
    this.sessionStatus.set('paused');
    this.updateDisplay(this.accumulatedSeconds);
  }

  doSessionAction(action: 'start'|'pause'|'resume'|'stop') {
    if ((action === 'start' || action === 'resume') && (this.shiftStatusLoading() || !this.shiftActive())) {
      showToast('error', `Start your shift before ${action === 'resume' ? 'resuming' : 'starting'} project work.`);
      return;
    }
    this.api.workSession({ action, project_id: this.projectId() }).subscribe({
      next: (res: any) => {
        if (!res.success) { showToast('error', res.message || 'Session error'); return; }
        if (action === 'start') {
          this.sessionStarted = new Date();
          this.accumulatedSeconds = Number(res.data?.current_total_seconds ?? res.data?.total_seconds ?? 0);
          this.sessionStatus.set('active');
          this.startTicking();
          showToast('success', 'Work session started!');
        } else if (action === 'pause') {
          this.accumulatedSeconds = res.data?.total_seconds ?? this.accumulatedSeconds;
          this.sessionStatus.set('paused');
          this.stopTicking();
          this.updateDisplay(this.accumulatedSeconds);
          showToast('success', 'Session paused.');
        } else if (action === 'resume') {
          this.sessionStarted = new Date();
          this.accumulatedSeconds = Number(res.data?.current_total_seconds ?? res.data?.total_seconds ?? this.accumulatedSeconds);
          this.sessionStatus.set('active');
          this.startTicking();
          showToast('success', 'Resumed!');
        } else if (action === 'stop') {
          this.stopTicking();
          this.sessionStatus.set('none');
          this.accumulatedSeconds = 0;
          this.timerDisplay.set('00:00:00');
          showToast('success', `Session stopped. ${res.data?.hours || 0}h logged.`);
          this.loadProject();
        }
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Network error updating session.';
        if (/start your shift/i.test(message)) this.shiftActive.set(false);
        showToast('error', message);
      }
    });
  }

  openSafetyConfirm() {
    if (this.shiftStatusLoading() || !this.shiftActive()) {
      showToast('error', 'Start your shift before starting project work.');
      return;
    }
    this.safetyAccepted.set(false);
    this.resetSafetyDecline();
    this.showSafetyConfirm.set(true);
  }

  closeSafetyConfirm() {
    if (this.isSaving()) return;
    this.showSafetyConfirm.set(false);
    this.safetyAccepted.set(false);
    this.resetSafetyDecline();
  }

  confirmSafetyAndStart() {
    if (!this.safetyAccepted() || this.isSaving()) return;
    if (this.shiftStatusLoading() || !this.shiftActive()) {
      this.showSafetyConfirm.set(false);
      showToast('error', 'Start your shift before starting project work.');
      return;
    }
    this.isSaving.set(true);
    this.api.acceptSafetyAcknowledgement({
      reference_id: this.projectId(),
      model_type: 'Project',
      statement: this.safetyStatement
    }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (!res.success) {
          showToast('error', res.message || 'Safety acknowledgement failed.');
          return;
        }
        this.showSafetyConfirm.set(false);
        this.doSessionAction('start');
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network failure recording safety acknowledgement.');
      }
    });
  }

  private loadShiftStatus() {
    this.shiftStatusLoading.set(true);
    this.api.getShiftStatus().subscribe({
      next: (res: any) => {
        this.shiftActive.set(!!(res?.success && res?.data?.active));
        this.shiftStatusLoading.set(false);
      },
      error: () => {
        this.shiftActive.set(false);
        this.shiftStatusLoading.set(false);
      }
    });
  }

  declineSafetyAcknowledgement() {
    const reason = this.safetyDeclineReason.trim();
    if (!this.projectId() || !reason || this.isSaving()) return;

    this.isSaving.set(true);
    this.api.acceptSafetyAcknowledgement({
      reference_id: this.projectId(),
      model_type: 'Project',
      statement: this.safetyStatement,
      status: 'Declined',
      decline_reason: reason,
      decline_photo_data: this.safetyDeclinePhoto()
    }).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (!res.success) {
          showToast('error', res.message || 'Safety decline could not be recorded.');
          return;
        }
        this.showSafetyConfirm.set(false);
        this.safetyAccepted.set(false);
        this.resetSafetyDecline();
        showToast('success', 'Safety concern sent to admin.');
        this.loadProject();
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network failure recording safety concern.');
      }
    });
  }

  onSafetyPhotoSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.safetyDeclinePhoto.set(reader.result as string);
      this.safetyDeclinePhotoName.set(file.name);
    };
    reader.readAsDataURL(file);
  }

  clearSafetyPhoto() {
    this.safetyDeclinePhoto.set(null);
    this.safetyDeclinePhotoName.set('');
  }

  private resetSafetyDecline() {
    this.safetyDeclineReason = '';
    this.clearSafetyPhoto();
  }

  startTicking() {
    this.stopTicking();
    this.refreshTimerDisplay();
    this.timerInterval = setInterval(() => this.refreshTimerDisplay(), 1000);
  }

  stopTicking() {
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
  }

  private refreshTimerDisplay() {
    const startedAt = this.sessionStarted?.getTime();
    const elapsed = startedAt && !Number.isNaN(startedAt)
      ? this.accumulatedSeconds + Math.floor((Date.now() - startedAt) / 1000)
      : this.accumulatedSeconds;
    this.updateDisplay(Math.max(0, elapsed));
  }

  private parseServerDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value)
      ? value
      : value.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  updateDisplay(totalSeconds: number) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    this.timerDisplay.set(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
  }

  ngOnDestroy() {
    this.clearProjectPhotoSelection();
    this.stopTicking();
    if (this.sessionSyncInterval) clearInterval(this.sessionSyncInterval);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
    window.removeEventListener('nzp-project-session-changed', this.handleProjectSessionChanged);
  }

  goBack() { this.location.back(); }

  openPhaseForm(phase: any) {
    this.activePhase.set(phase);
    // Pre-fill from existing form_data if any
    const fd = phase.form_data;
    if (fd) {
      this.formData.set({
        techDetails: fd.tech_details || fd.techDetails || this.formData().techDetails,
        checks: fd.checks || fd.selections || {},
        notes: fd.notes || ''
      });
    } else {
      this.formData.set({
        techDetails: { ...this.formData().techDetails },
        checks: {},
        notes: ''
      });
    }
    this.sigEmpty = true;
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
    this.activePhase.set(null);
  }

  getCheck(sectionKey: string, item: string): boolean {
    return this.formData().checks?.[sectionKey]?.[item] ?? false;
  }

  setCheck(sectionKey: string, item: string, val: boolean) {
    this.formData.update(f => {
      const checks = { ...f.checks };
      if (!checks[sectionKey]) checks[sectionKey] = {};
      checks[sectionKey] = { ...checks[sectionKey], [item]: val };
      return { ...f, checks };
    });
  }

  submitPhaseForm(workflowAction: 'save_draft' | 'submit') {
    const phase = this.activePhase();
    if (!phase) return;
    this.isSaving.set(true);

    let sigBase64 = '';
    if (this.sigPad?.nativeElement) {
      sigBase64 = this.sigPad.nativeElement.toDataURL('image/png');
    }
    if (workflowAction === 'submit' && (!sigBase64 || this.sigEmpty)) {
      showToast('error', 'Signature is required before submitting this phase.');
      this.isSaving.set(false);
      return;
    }

    const payload = {
      phase_id: phase.id,
      project_id: this.projectId(),
      workflow_action: workflowAction,
      form_data: {
        tech_details: this.formData().techDetails,
        checks: this.formData().checks,
        notes: this.formData().notes,
        signature: this.sigEmpty ? '' : sigBase64
      }
    };

    this.api.saveProjectPhaseForm(payload).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          showToast('success', workflowAction === 'submit' ? 'Form submitted for office review.' : 'Draft saved.');
          this.closeModal();
          this.loadProject();
        } else {
          showToast('error', res.message || 'Failed to save form.');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error saving form.');
      }
    });
  }

  // ── Signature helpers ──
  initSigCanvas() {
    if (!this.sigPad?.nativeElement) return;
    const canvas = this.sigPad.nativeElement;
    const parent = canvas.parentElement!;
    if (!parent.clientWidth || !parent.clientHeight) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = parent.clientWidth * ratio;
    canvas.height = parent.clientHeight * ratio;
    this.sigCtx = canvas.getContext('2d')!;
    this.sigCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.sigCtx.lineWidth = 3;
    this.sigCtx.lineCap = 'round';
    this.sigCtx.lineJoin = 'round';
    this.sigCtx.strokeStyle = '#0f172a';
    canvas.ontouchstart = e => e.preventDefault();
    canvas.ontouchmove = e => e.preventDefault();
  }

  startSig(e: any) {
    if (!this.sigCtx) this.initSigCanvas();
    if (!this.sigCtx) return;
    this.sigDrawing = true;
    this.sigEmpty = false;
    const p = this.getSigPos(e);
    this.sigCtx.beginPath();
    this.sigCtx.moveTo(p.x, p.y);
  }
  drawSig(e: any) { if (!this.sigDrawing || !this.sigCtx) return; const p = this.getSigPos(e); this.sigCtx.lineTo(p.x, p.y); this.sigCtx.stroke(); }
  stopSig() { this.sigDrawing = false; }
  clearSig() {
    if (this.sigPad?.nativeElement && this.sigCtx) {
      const rect = this.sigPad.nativeElement.getBoundingClientRect();
      this.sigCtx.clearRect(0, 0, rect.width, rect.height);
    }
    this.sigEmpty = true;
  }
  getSigPos(e: any) {
    const rect = this.sigPad.nativeElement.getBoundingClientRect();
    if (e.touches?.length) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
}
